import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Tabs,
  Tab,
  Divider,
  IconButton,
  Button,
  TextField,
  Stack,
  InputAdornment,
  Tooltip,
  Chip,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { getOrganizationName } from "../../../utils/organizationUtils";
import { downloadStyledTableExcel } from "../../../utils/patientTableExcelExport";
import { getTreatmentsByAdmissionId } from "../../../services/PatientTreatmentService";
import { getPatientById } from "../../../services/patientService";
import { getAdmissionByPatientId } from "../../../services/PatientAdmissionService";
import { getAssignmentByAdmissionId } from "../../../services/PatientAssignmentService";
import Header from "../../../components/Header";
import dayjs from "dayjs";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import IpdIssueMedicineModal from "./IpdIssueMedicineModal";
import MedicineReturnModal from "./MedicineReturnModal";
// import MedicineRequestModal from "./MedicineRequestModal"; // COMMENTED OUT - Actions now in IpdReports.jsx
import { getRequestsByAdmissionId } from "../../../services/medicineRequestService";
// import { receiveMedicineRequest, dispenseMedicineRequest } from "../../../services/medicineRequestService"; // COMMENTED OUT - Actions now in IpdReports.jsx
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import DownloadIcon from "@mui/icons-material/Download";
import Refresh from "@mui/icons-material/Refresh";
import UndoIcon from "@mui/icons-material/Undo";
import MedicationIcon from "@mui/icons-material/Medication";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import {
  TABLE_ICON_BUTTON_SX,
  TABLE_ICON_SX,
  TABLE_SEARCH_ROW_SX,
} from "../../../utils/pageLayoutStyles";
const tabLabels = ['Treatments & Medicines'];

const IpdTreatments = ({ patientId: propPatientId, onBack }) => {
  const { patientId: paramPatientId } = useParams();
  const patientId = propPatientId || paramPatientId; // Use prop if provided, otherwise use URL param
  const navigate = useNavigate();
  const [treatments, setTreatments] = useState([]);
  const [patientInfo, setPatientInfo] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [selectedTreatment, setSelectedTreatment] = useState(null);
  const [openReturnModal, setOpenReturnModal] = useState(false);
  const [selectedReturnTreatment, setSelectedReturnTreatment] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterDoctor, setFilterDoctor] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [admissions, setAdmissions] = useState([]);
  const [assignmentList, setAssignmentList] = useState({}); // ✅ Store assignments by admissionId
  
  // Medicine request workflow states
  // const [openRequestModal, setOpenRequestModal] = useState(false); // COMMENTED OUT - Actions now in IpdReports.jsx
  // const [selectedRequestTreatment, setSelectedRequestTreatment] = useState(null); // COMMENTED OUT - Actions now in IpdReports.jsx
  const [medicineRequests, setMedicineRequests] = useState({}); // Map: treatmentId -> request object
  // const { user: currentUser } = useAuth(); // COMMENTED OUT - Not needed for status display only

  useEffect(() => {
    fetchPatientInfo();
    fetchPatientAdmissions();
  }, [patientId]);

  const fetchPatientInfo = async () => {
    try {
      const data = await getPatientById(patientId);
      setPatientInfo(data);
    } catch (error) {
      console.error("Failed to fetch patient info", error);
    }
  };

  const fetchPatientAdmissions = async () => {
    try {
      const data = await getAdmissionByPatientId(patientId);
      setAdmissions(data);
      
      // Fetch treatments, medicine requests, and assignments for all admissions
      const allTreatments = [];
      const allRequests = {};
      const allAssignments = {}; // ✅ Store assignments
      
      for (const admission of data) {
        try {
          const treatments = await getTreatmentsByAdmissionId(admission.id);
          if (treatments && treatments.length > 0) {
            // Add admission info to each treatment
            const treatmentsWithAdmission = treatments.map(treatment => ({
              ...treatment,
              admissionId: admission.id,
              admissionDate: admission.admissionDate,
              admissionStatus: admission.status
            }));
            allTreatments.push(...treatmentsWithAdmission);
          }

          // Fetch medicine requests for this admission
          try {
            const requests = await getRequestsByAdmissionId(admission.id);
            if (requests && requests.length > 0) {
              // Map requests by treatmentId for quick lookup
              requests.forEach(request => {
                if (request.treatmentId) {
                  allRequests[request.treatmentId] = request;
                }
              });
            }
          } catch (requestError) {
            console.error(`Failed to fetch medicine requests for admission ${admission.id}`, requestError);
          }

          // ✅ Fetch assignment for this admission to get doctor name
          try {
            const assignment = await getAssignmentByAdmissionId(admission.id);
            if (assignment) {
              // Handle both array and single object responses
              const assignmentData = Array.isArray(assignment) && assignment.length > 0 
                ? assignment[0] 
                : assignment;
              allAssignments[admission.id] = assignmentData;
            }
          } catch (assignmentError) {
            console.error(`Failed to fetch assignment for admission ${admission.id}`, assignmentError);
          }
        } catch (error) {
          console.error(`Failed to fetch treatments for admission ${admission.id}`, error);
        }
      }
      
      // Sort treatments by date (newest first)
      const sorted = allTreatments.sort((a, b) => {
        const dateA = a.treatmentDate ? new Date(a.treatmentDate) : new Date(0);
        const dateB = b.treatmentDate ? new Date(b.treatmentDate) : new Date(0);
        return dateB - dateA;
      });
      
      setTreatments(sorted);
      setMedicineRequests(allRequests);
      setAssignmentList(allAssignments); // ✅ Store assignments
    } catch (error) {
      console.error("Failed to fetch patient admissions", error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleIssueMedicine = (treatment) => {
    setSelectedTreatment(treatment);
    setOpenModal(true);
  };

  const handleModalClose = () => {
    setOpenModal(false);
    setSelectedTreatment(null);
  };

  const handleMedicineSubmit = async (medicineData) => {
    try {
      await fetchPatientAdmissions(); // Refresh the treatments list
    } catch (error) {
      console.error("Error handling medicine submission:", error);
    }
  };

  const handleReturnMedicine = (treatment) => {
    setSelectedReturnTreatment({
      ...treatment,
      medicineRequest: medicineRequests[treatment.id], // Pass the associated request
    });
    setOpenReturnModal(true);
  };

  const handleReturnModalClose = () => {
    setOpenReturnModal(false);
    setSelectedReturnTreatment(null);
  };

  const handleReturnSubmit = async (returnData) => {
    try {
      await fetchPatientAdmissions(); // Refresh the treatments list
    } catch (error) {
      console.error("Error handling medicine return:", error);
    }
  };

  // Medicine request handlers - COMMENTED OUT (Actions now available in IpdReports.jsx)
  // const handleRequestMedicine = (treatment) => {
  //   setSelectedRequestTreatment(treatment);
  //   setOpenRequestModal(true);
  // };

  // const handleRequestModalClose = () => {
  //   setOpenRequestModal(false);
  //   setSelectedRequestTreatment(null);
  // };

  // const handleRequestSubmit = async (requestData) => {
  //   try {
  //     await fetchPatientAdmissions(); // Refresh to get updated requests
  //   } catch (error) {
  //     console.error("Error handling medicine request:", error);
  //   }
  // };

  // const handleReceiveMedicine = async (request) => {
  //   try {
  //     const receiveData = {
  //       receivedByUserId: currentUser?.id || 0,
  //       receivedByName: currentUser?.name || currentUser?.username || "Unknown",
  //       receivedDate: new Date().toISOString(),
  //     };

  //     await receiveMedicineRequest(request.id, receiveData);
  //     await fetchPatientAdmissions(); // Refresh to update status
  //     alert("Medicine received successfully!");
  //   } catch (error) {
  //     console.error("Error receiving medicine:", error);
  //     alert("Failed to mark medicine as received. Please try again.");
  //   }
  // };

  // const handleDispenseToPatient = async (request) => {
  //   try {
  //     const dispenseData = {
  //       dispensedByUserId: currentUser?.id || 0,
  //       dispensedByName: currentUser?.name || currentUser?.username || "Unknown",
  //       dispensedDate: new Date().toISOString(),
  //     };

  //     await dispenseMedicineRequest(request.id, dispenseData);
  //     await fetchPatientAdmissions(); // Refresh to update status
  //     alert("Medicine dispensed to patient successfully!");
  //   } catch (error) {
  //     console.error("Error dispensing medicine:", error);
  //     alert("Failed to mark medicine as dispensed. Please try again.");
  //   }
  // };

  // Build filtered + sorted list
  const filteredList = (treatments || []).filter((t) => {
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const vals = [t.medicine, t.dosage, t.treatmentType, t.performedByName, t.remarks]
        .filter(Boolean)
        .map(v => String(v).toLowerCase());
      if (!vals.some(v => v.includes(q))) return false;
    }
    if (filterDoctor.trim()) {
      if (!String(t.performedByName || "").toLowerCase().includes(filterDoctor.toLowerCase())) return false;
    }
    if (fromDate || toDate) {
      const d = t.treatmentDate ? new Date(t.treatmentDate) : null;
      if (!d) return false;
      if (fromDate && d < new Date(fromDate)) return false;
      if (toDate) { 
        const toEnd = new Date(toDate); 
        toEnd.setHours(23,59,59,999); 
        if (d > toEnd) return false; 
      }
    }
    return true;
  });

  // Group treatments by date
  const groupedTreatments = filteredList.reduce((acc, treatment) => {
    const date = treatment.treatmentDate ? dayjs(treatment.treatmentDate).format("YYYY-MM-DD") : "Unknown Date";
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(treatment);
    return acc;
  }, {});

  const clearFilters = () => {
    setSearchQuery("");
    setFilterDoctor("");
    setFromDate("");
    setToDate("");
  };

  const handleExport = async () => {
    const rows = filteredList || [];
    const headers = ["Treatment Type", "Medicine", "Dosage", "Performed By", "Date", "Time", "Remarks"];
    const tableRows = rows.map((t) => [
      t.treatmentType || "",
      t.medicine || "",
      t.dosage || "",
      t.performedByName || "",
      t.treatmentDate || "",
      t.treatmentTime ? `${t.treatmentTime[0]}:${String(t.treatmentTime[1]).padStart(2, "0")}` : "",
      t.remarks || "",
    ]);

    await downloadStyledTableExcel({
      filename: `ipd_treatments_${dayjs().format("YYYY-MM-DD")}`,
      sheetName: "IPD Treatments",
      title: "IPD Treatments",
      organizationName: getOrganizationName(),
      tableHeaders: headers,
      tableRows,
    });
  };

  return (
    <Box sx={{ p: 4, backgroundColor: "#fff", minHeight: "100vh" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Header
          title={
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton
                onClick={() => onBack ? onBack() : navigate(-1)}
                sx={{
                  backgroundColor: "#1976d2",
                  color: "#fff",
                  mr: 1,
                  p: 0.5,
                  "&:hover": {
                    backgroundColor: "#1565c0",
                  },
                }}
              >
                <ArrowBackIcon fontSize="small" />
              </IconButton>
              <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                {
                  patientInfo.name
                    ?.split(" ")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                    .join(" ")
                } - IPD Treatments
              </Typography>
            </Box>
          }
        />
      </Box>
      
      <Paper sx={{ p: 3, borderRadius: 2, boxShadow: 3 }}>
        <Tabs 
          variant="scrollable"
          scrollButtons="auto"
          indicatorColor="primary"
          sx={{ mb: 2 }} 
          value={activeTab} 
          onChange={handleTabChange}>
          {tabLabels.map((label, index) => (
            <Tab label={label} key={index} />
          ))}
        </Tabs>
        <Divider sx={{ my: 2 }} />

        <Box sx={TABLE_SEARCH_ROW_SX}>
          <TextField
            placeholder="Search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            fullWidth
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={TABLE_ICON_SX} fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title={showFilters ? "Hide filters" : "Show filters"}>
            <IconButton
              size="small"
              sx={TABLE_ICON_BUTTON_SX}
              onClick={() => setShowFilters((v) => !v)}
            >
              <FilterAltIcon
                fontSize="small"
                color={showFilters ? "primary" : "inherit"}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export to Excel">
            <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={handleExport}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton
              size="small"
              sx={TABLE_ICON_BUTTON_SX}
              onClick={async () => await fetchPatientAdmissions()}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        {showFilters && (
        <Stack spacing={1.5} direction={{ xs:'column', sm:'row' }} alignItems={{ xs:'stretch', sm:'center' }} sx={{ mb: 2 }}>
          <TextField label="Doctor name" value={filterDoctor} onChange={(e)=>setFilterDoctor(e.target.value)} />
          <TextField label="From date" type="date" value={fromDate} onChange={(e)=>setFromDate(e.target.value)} InputLabelProps={{shrink:true}} />
          <TextField label="To date" type="date" value={toDate} onChange={(e)=>setToDate(e.target.value)} InputLabelProps={{shrink:true}} />
          <Tooltip title="Clear filters"><IconButton onClick={clearFilters}><ClearAllIcon/></IconButton></Tooltip>
        </Stack>
        )}
        
        {activeTab === 0 && (
          <Box sx={{ position: 'relative' }}>
            {Object.keys(groupedTreatments).length === 0 ? (
              <Typography>No treatments available</Typography>
            ) : (
              Object.entries(groupedTreatments).map(([date, treatments]) => (
                <Box key={date} sx={{ mb: 3, borderRadius: 2, p: 2 }}>
                  {/* Vertical Line */}
                  <Box
                    sx={{
                      position: "absolute",
                      left: "-10px",
                      top: '0',
                      height: '100%',
                      width: '2px',
                      backgroundColor: '#1976d2',
                    }}
                  />
                  
                  <Typography variant="body2" fontWeight="bold" sx={{ color: "#1976d2", mb: 0.5 }}>
                    {date}
                  </Typography>

                  {treatments.map((treatment, index) => (
                    <Box key={index} sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" fontWeight="bold">
                        Treatment #{index + 1} - {treatment.treatmentType} - By {treatment.performedByName}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          Date: {treatment.treatmentDate ? dayjs(treatment.treatmentDate).format("DD MMM YYYY") : "N/A"} 
                          {treatment.treatmentTime && (
                            <> at {dayjs().hour(treatment.treatmentTime[0]).minute(treatment.treatmentTime[1]).format("hh:mm A")}</>
                          )}
                        </Typography>
                        {treatment.medicine && (() => {
                          const request = medicineRequests[treatment.id];
                          
                          return (
                            <Box sx={{ display: 'flex', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                              {/* Medicine Request Status Display - Actions available in IpdReports.jsx */}
                              {/* Show Request Status Badge if exists */}
                              {request && (
                                <Chip 
                                  label={`Status: ${request.status}`}
                                  color={
                                    request.status === 'REQUESTED' ? 'warning' :
                                    request.status === 'APPROVED' ? 'info' :
                                    request.status === 'ISSUED' ? 'success' :
                                    request.status === 'RECEIVED' ? 'primary' :
                                    request.status === 'DISPENSED' ? 'default' :
                                    request.status === 'REJECTED' ? 'error' :
                                    'default'
                                  }
                                  size="small"
                                  sx={{ mr: 1 }}
                                />
                              )}

                              {/* No request yet - Show status */}
                              {!request && (
                                <Chip 
                                  label="No Request Yet"
                                  color="default"
                                  variant="outlined"
                                  size="small"
                                />
                              )}

                              {/* Request REQUESTED - Show waiting status */}
                              {request?.status === 'REQUESTED' && (
                                <Chip 
                                  label="Waiting for Pharmacy Approval"
                                  color="warning"
                                  icon={<MedicationIcon />}
                                  size="small"
                                />
                              )}

                              {/* Request APPROVED - Show waiting for issue */}
                              {request?.status === 'APPROVED' && (
                                <Chip 
                                  label="Approved - Waiting for Pharmacy to Issue"
                                  color="info"
                                  icon={<CheckCircleIcon />}
                                  size="small"
                                />
                              )}

                              {/* Request ISSUED - Show issued status */}
                              {request?.status === 'ISSUED' && (
                                <Chip 
                                  label="Issued - Ready to Receive"
                                  color="success"
                                  icon={<LocalShippingIcon />}
                                  size="small"
                                />
                              )}

                              {/* Request RECEIVED - Show received status */}
                              {request?.status === 'RECEIVED' && (
                                <Chip 
                                  label="Received - Ready to Dispense"
                                  color="primary"
                                  icon={<MedicationIcon />}
                                  size="small"
                                />
                              )}

                              {/* Request DISPENSED - Show dispensed status */}
                              {request?.status === 'DISPENSED' && (
                                <Chip 
                                  label="Dispensed to Patient"
                                  color="default"
                                  icon={<MedicationIcon />}
                                  size="small"
                                />
                              )}

                              {/* Request REJECTED - Show rejected status */}
                              {request?.status === 'REJECTED' && (
                                <Chip 
                                  label="Request Rejected by Pharmacy"
                                  color="error"
                                  icon={<MedicationIcon />}
                                  size="small"
                                />
                              )}

                              {/* Request RETURNED - Show completion status */}
                              {request?.status === 'RETURNED' && (
                                <Chip 
                                  label="Medicine Returned to Pharmacy"
                                  color="warning"
                                  icon={<UndoIcon />}
                                  size="small"
                                />
                              )}
                            </Box>
                          );
                        })()}
                      </Box>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#3498db' }}>
                            <TableCell sx={{ color: "#fff" }}>Treatment Type</TableCell>
                            <TableCell sx={{ color: "#fff" }}>Medicine Name</TableCell>
                            <TableCell sx={{ color: "#fff" }}>Dosage</TableCell>
                            <TableCell sx={{ color: "#fff" }}>Frequency</TableCell>
                            <TableCell sx={{ color: "#fff" }}>Duration</TableCell>
                            <TableCell sx={{ color: "#fff" }}>Remarks</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          <TableRow>
                            <TableCell>{treatment.treatmentType}</TableCell>
                            <TableCell>{treatment.medicine}</TableCell>
                            <TableCell>{treatment.dosage}</TableCell>
                            <TableCell>{treatment.frequency || "As prescribed"}</TableCell>
                            <TableCell>{treatment.duration || "As prescribed"}</TableCell>
                            <TableCell>{treatment.remarks}</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Box>
                  ))}
                </Box>
              ))
            )}
          </Box>
        )}

      </Paper>

      {/* IPD Issue Medicine Modal */}
      <IpdIssueMedicineModal
        open={openModal}
        onClose={handleModalClose}
        treatment={selectedTreatment}
        onSubmit={handleMedicineSubmit}
        patientId={patientInfo.id}
        patientName={patientInfo?.name}
        doctorName={selectedTreatment?.admissionId ? assignmentList[selectedTreatment.admissionId]?.doctorName : null} // ✅ Pass doctor name from assignment
        doctorId={
          selectedTreatment?.admissionId
            ? assignmentList[selectedTreatment.admissionId]?.doctorId ||
              assignmentList[selectedTreatment.admissionId]?.doctor_id ||
              null
            : null
        }
        admissionId={selectedTreatment?.admissionId || null}
        prescriptionId={
          selectedTreatment?.prescriptionId ||
          selectedTreatment?.prescription_id ||
          selectedTreatment?.ipdPrescriptionId ||
          selectedTreatment?.ipd_prescription_id ||
          null
        }
        departmentId={
          selectedTreatment?.admissionId
            ? assignmentList[selectedTreatment.admissionId]?.departmentId ||
              assignmentList[selectedTreatment.admissionId]?.department_id ||
              null
            : null
        }
      />

      {/* Medicine Return Modal */}
      <MedicineReturnModal
        open={openReturnModal}
        onClose={handleReturnModalClose}
        treatment={selectedReturnTreatment}
        onSubmit={handleReturnSubmit}
        patientId={patientInfo.id}
        medicineRequest={selectedReturnTreatment?.medicineRequest}
      />

      {/* Medicine Request Modal - COMMENTED OUT (Actions now available in IpdReports.jsx) */}
      {/* <MedicineRequestModal
        open={openRequestModal}
        onClose={handleRequestModalClose}
        treatment={selectedRequestTreatment}
        patientId={patientInfo.id}
        admissionId={selectedRequestTreatment?.admissionId}
        onSubmit={handleRequestSubmit}
      /> */}
      
    </Box>
  );
};

export default IpdTreatments;
